import { Chip, IconButton, Table, TableBody, TableCell, TableContainer, TablePagination, TableRow } from "@mui/material";
import { DeleteOutline, Preview } from "@mui/icons-material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import TableWrapperStyle from "./style";
import { Order } from "./types";
import { Data } from "./types";
import headCells from "./headCells";

import EnhancedTableHead from "@/components/app/EnhancedTableHeader";
import TableSkeleton from "@/components/app/TableSkeleton";
import InitLoading from "@/components/app/Loading/pageLoading";
import ConfirmationModal from "@/components/app/modalVariants/confirmation";
import { useCheckPermissionAccess } from "@/hooks/useCheckPermissionAccess";
import { OperationAccess, PagesAccess } from "@/constants/enums/permissions";
import ROUTE_CONSTANTS from "@/constants/route-constants";
import { IContentListResponse } from "@/api/types/content";

export function createData(data: IContentListResponse): Data {
    const { id, ml_name, isEducation, updatedAt, thumbnailPortrait, typeId } = data;
    const { en, ar } = ml_name;

    return {
        id,
        en_title: en,
        ar_title: ar,
        thumbnailPortrait: thumbnailPortrait?.url || "",
        category: isEducation ? "Education" : "Entertainment",
        updated_at: dayjs(updatedAt).format("YYYY-MM-DD HH:mm"),
        typeId,
    };
}

export default function ShowContents({
    data: rows,
    totalItems,
    rowsPerPage,
    page,
    onChange,
    isLoading,
    isFetching,
    isPending,
}: {
    data: Data[],
    isPending: boolean,
    isLoading: boolean,
    isFetching: boolean,
    totalItems: number,
    page: number,
    rowsPerPage: number,
    // eslint-disable-next-line no-unused-vars
    onChange: (data: { page?: number, rowsPerPage?: number }) => void,
}) {
    const [order, setOrder] = useState<Order>("desc");
    const [orderBy, setOrderBy] = useState<keyof Data>("updated_at");
    const [confirmModalId, setConfirmModal] = useState<number | null>(null);

    const navigate = useNavigate();

    const deleteAccess = useCheckPermissionAccess(PagesAccess.CONTENT, OperationAccess.DELETE);
    // const editAccess = useCheckPermissionAccess(PagesAccess.CONTENT, OperationAccess.UPDATE);

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
        const isAsc = orderBy === property && order === "asc";

        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleClose = () => setConfirmModal(null);
    const handleDelete = () => {};

    const handlePreview = (id: number) => navigate(ROUTE_CONSTANTS.CONTENT_MANAGEMENT.CONTENTS.SHOW.SHOW_BY_ID(id).ABSOLUTE);

    const handleChangePage = (event: unknown, newPage: number) => onChange({ page: newPage, rowsPerPage });
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) =>
        onChange({ page: 0, rowsPerPage: parseInt(event.target.value, 10) });

    return (
        <TableWrapperStyle className="table-style-wrapper" sx={{ width: "100%", marginTop: "2em" }}>
            <TableContainer sx={{ maxHeight: "700px" }}>
                <Table
                    stickyHeader
                    aria-labelledby="tableTitle"
                    className="table"
                    component="div"
                    size="medium"
                    sx={{ padding: ".5em" }}
                >
                    <EnhancedTableHead<Data>
                        headCells={headCells}
                        order={order}
                        orderBy={orderBy}
                        rowCount={rows.length}
                        onRequestSort={handleRequestSort}
                    />
                    <TableBody component="div">
                        {isLoading && <TableSkeleton cells={headCells.map((item) => item.id)} />}
                        {!isLoading && !isFetching && rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={headCells.length}>No Data To Show</TableCell>
                            </TableRow>
                        )}
                        {isFetching && <InitLoading />}
                        {rows.map((row) => {
                            const { id, en_title, ar_title, category, updated_at, typeId, thumbnailPortrait } = row;

                            return (
                                <TableRow key={id} className="table-row" component="div" role="checkbox" tabIndex={-1}>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{id}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value image">
                                            {thumbnailPortrait ? (
                                                <img alt={en_title} src={thumbnailPortrait} />
                                            ) : (
                                                <div style={{ width: "40px", height: "40px" }} />
                                            )}
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{en_title || "-"}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{ar_title || "-"}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{typeId === 1 ? "Movie" : "Serial"}</span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value" style={{ padding: "13px" }}>
                                            <Chip
                                                color={category.toLocaleLowerCase() === "education" ? "success" : "info"}
                                                label={category}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </span>
                                    </TableCell>
                                    <TableCell align="center" className="table-cell" component="div">
                                        <span className="table-value">{updated_at}</span>
                                    </TableCell>
                                    <TableCell align="right" className="table-cell" component="div">
                                        <span className="table-value" style={{ padding: "5px", justifyContent: "flex-end" }}>
                                            <IconButton onClick={() => handlePreview(id)}>
                                                <Preview />
                                            </IconButton>
                                            <IconButton disabled={!deleteAccess} onClick={() => setConfirmModal(id)}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </span>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={totalItems}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <ConfirmationModal
                description="Are you sure to delete this Item ?"
                handleClose={handleClose}
                loading={isPending}
                open={Boolean(confirmModalId)}
                onConfirm={handleDelete}
            />
        </TableWrapperStyle>
    );
}
